import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getStages, createStage, updateStage, deleteStage, getLeadSources, createLeadSource, updateLeadSource, deleteLeadSource, getOwnershipRoleTypes, createOwnershipRoleType, updateOwnershipRoleType, deleteOwnershipRoleType, getRoles, createRole, updateRole, deleteRole, getOpportunityStagesSettings, createOpportunityStage, updateOpportunityStage, deleteOpportunityStage, getUsers, createUser, updateUser, resetUserPassword, deactivateUser, reactivateUser, getCompanySettings, updateCompanySettings, uploadCompanyLogo, getRepFirms, createRepFirm, getReportAccess, updateReportAccess, getFactoryResetPreview, factoryReset, getLoginHistory, inviteUser } from '../api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import AIUsagePanel from '../components/AIUsagePanel';
import AccessRequestsPanel from '../components/AccessRequestsPanel';
import CustomersPanel from '../components/CustomersPanel';
import BillingPanel from '../components/BillingPanel';
import InviteLinkDialog from '../components/InviteLink';
import EmailVerificationNotice from '../components/EmailVerificationNotice';
import { inviteActionLabel } from '../utils/emailLinks';
import { Plus, Edit, Trash2, Key, UserX, UserCheck, Shield, Settings as SettingsIcon, Upload, Building2, Save, FileText, Target, ClipboardList, Users, UserCog, Briefcase, Layers, ChevronRight, Search, BarChart3, Eye, AlertTriangle, History, CheckCircle2, XCircle, Download, Sparkles, Inbox, Building, Mail, CreditCard } from 'lucide-react';

// New users get an emailed invite by default and choose their own password.
const EMPTY_USER_FORM = { first_name: '', last_name: '', alias: '', email: '', password: '', role_id: '', send_invite: true };

// Sections that ?section= can open, for links such as Stripe's return to
// /settings?section=billing. Any other value opens Company Info.
const LINKABLE_SECTIONS = ['company', 'stages', 'opportunity-stages', 'lead-sources', 'ownership-types', 'rep-firms', 'roles', 'report-access', 'ai-usage'];

function Settings() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, user: currentUser, startImpersonation, isImpersonating, isPlatformOwner } = useAuth();
  const billingApplies = !!currentUser?.billing?.applies;
  const [activeSection, setActiveSection] = useState(() => {
    const wanted = new URLSearchParams(location.search).get('section');
    if (wanted === 'billing') return billingApplies ? 'billing' : 'company';
    if (wanted === 'customers') return isPlatformOwner ? 'customers' : 'company';
    return LINKABLE_SECTIONS.includes(wanted) ? wanted : 'company';
  });
  const [stages, setStages] = useState([]);
  const [opportunityStages, setOpportunityStages] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [ownershipTypes, setOwnershipTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [roleForm, setRoleForm] = useState({ name: '', view_accounts: true, edit_accounts: false, delete_accounts: false, view_all_accounts: false, manage_users: false, manage_settings: false });
  const [oppStageForm, setOppStageForm] = useState({ name: '', is_won: false, is_lost: false });
  const [userForm, setUserForm] = useState(EMPTY_USER_FORM);
  // Invite outcome: a short confirmation when it was emailed, or the link
  // to copy when email is not set up on the server.
  const [inviteNotice, setInviteNotice] = useState('');
  const [inviteLink, setInviteLink] = useState(null);
  const [invitingId, setInvitingId] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [deactivateConfirm, setDeactivateConfirm] = useState(null);
  const [companySettings, setCompanySettings] = useState({
    company_name: '',
    company_address: '',
    company_email: '',
    company_website: '',
    company_phone: '',
    company_tagline: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [companySaving, setCompanySaving] = useState(false);
  const [companyMessage, setCompanyMessage] = useState('');
  const [repFirms, setRepFirms] = useState([]);
  const [repFirmSearch, setRepFirmSearch] = useState('');
  const [showRepFirmModal, setShowRepFirmModal] = useState(false);
  const [repFirmForm, setRepFirmForm] = useState({ name: '', website: '', phone: '', address: '', country: '', state: '', notes: '' });
  const [reportAccess, setReportAccess] = useState([]);
  const [reportAccessSaving, setReportAccessSaving] = useState(false);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);
  const [loginHistorySearch, setLoginHistorySearch] = useState('');

  // ── Factory reset state ──────────────────────────────────────────
  // resetPreview = { tables: {...}, preserved: {...}, total_rows_to_delete, confirm_phrase }
  // Loaded when the user opens the confirm modal; lets us show them
  // exactly what will be deleted before they type the phrase.
  const [resetPreview, setResetPreview] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPhrase, setResetPhrase] = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [resetError, setResetError] = useState('');

  // Keep activeTab as an alias for backward compatibility with handlers
  const activeTab = activeSection;

  useEffect(() => { loadData(); }, []);

  // Auto-fetch the factory-reset preview the first time the user lands
  // on the Danger Zone, and re-fetch whenever they return to it (so
  // counts are fresh after any data changes elsewhere).
  useEffect(() => {
    if (activeSection !== 'system-reset') return;
    let cancelled = false;
    getFactoryResetPreview()
      .then(res => { if (!cancelled) setResetPreview(res.data); })
      .catch(err => {
        if (!cancelled) setResetError(err.response?.data?.error || 'Could not load preview');
      });
    return () => { cancelled = true; };
  }, [activeSection]);

  // Load login history whenever the admin lands on that section, so the
  // list is always fresh (new attempts may have happened since last view).
  useEffect(() => {
    if (activeSection !== 'login-history') return;
    let cancelled = false;
    setLoginHistoryLoading(true);
    getLoginHistory({ limit: 200 })
      .then(res => { if (!cancelled) setLoginHistory(res.data); })
      .catch(() => { if (!cancelled) setError('Failed to load login history'); })
      .finally(() => { if (!cancelled) setLoginHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [activeSection]);

  // Export the given login-history rows to a CSV file the browser
  // downloads. Operates on whatever rows are passed in (i.e. the current
  // filtered view), so the export matches what the admin is looking at.
  const handleExportLoginHistory = (rows) => {
    const reasonLabels = { unknown_email: 'Unknown email', invalid_password: 'Wrong password' };
    const escapeCell = (val) => {
      const s = val == null ? '' : String(val);
      // Quote any field containing a comma, quote, or newline; double up quotes.
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ['When', 'Result', 'Failure reason', 'User', 'Email entered', 'IP address', 'Device'];
    const lines = rows.map(r => [
      new Date(r.created_at.replace(' ', 'T') + 'Z').toLocaleString(),
      r.success ? 'Success' : 'Failed',
      r.success ? '' : (reasonLabels[r.failure_reason] || r.failure_reason || ''),
      r.user_name || '',
      r.email_attempted || '',
      r.ip_address || '',
      r.user_agent || '',
    ].map(escapeCell).join(','));
    const csv = [headers.join(','), ...lines].join('\r\n');

    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().slice(0, 10);
    const link = document.createElement('a');
    link.href = url;
    link.download = `login-history-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Final-confirmation submit: type-phrase already validated, so this
  // just sends the request, redirects on success, surfaces error
  // otherwise.
  const performFactoryReset = async () => {
    setResetSaving(true);
    setResetError('');
    try {
      await factoryReset(resetPhrase);
      // Hard reload so every cached query starts from a clean slate.
      // Stay on the Settings page so the admin sees the empty system.
      window.location.href = '/settings';
      window.location.reload();
    } catch (err) {
      setResetError(err.response?.data?.error || 'Factory reset failed');
      setResetSaving(false);
    }
  };

  const loadData = async () => {
    try {
      const [stagesRes, oppStagesRes, sourcesRes, typesRes, rolesRes, usersRes] = await Promise.all([
        getStages(),
        getOpportunityStagesSettings(),
        getLeadSources(),
        getOwnershipRoleTypes(),
        getRoles(),
        hasPermission('manage_users') ? getUsers() : Promise.resolve({ data: [] }),
      ]);
      setStages(stagesRes.data);
      setOpportunityStages(oppStagesRes.data);
      setLeadSources(sourcesRes.data);
      setOwnershipTypes(typesRes.data);
      setRoles(rolesRes.data);
      setUsers(usersRes.data);
      try {
        const repFirmsRes = await getRepFirms({});
        setRepFirms(repFirmsRes.data);
      } catch (e) {
        console.error('Failed to load rep firms:', e);
      }
      try {
        const companyRes = await getCompanySettings();
        setCompanySettings(companyRes.data);
        if (companyRes.data.logo_path) {
          const serverBase = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');
          setLogoPreview(`${serverBase}${companyRes.data.logo_path}`);
        }
      } catch (e) {
        console.error('Failed to load company settings:', e);
      }
    } catch (err) { setError('Failed to load settings'); }
    finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      if (activeTab === 'stages') await createStage(newName);
      else if (activeTab === 'opportunity-stages') await createOpportunityStage(oppStageForm);
      else if (activeTab === 'lead-sources') await createLeadSource(newName);
      else if (activeTab === 'ownership-types') await createOwnershipRoleType(newName);
      else if (activeTab === 'roles') await createRole(roleForm);
      else if (activeTab === 'users') {
        setInviteNotice('');
        const { send_invite, password, ...fields } = userForm;
        const res = await createUser(send_invite ? { ...fields, send_invite: true } : { ...fields, password });
        const created = res.data || {};
        showInviteOutcome(created.invite, {
          name: created.name || [fields.first_name, fields.last_name].filter(Boolean).join(' '),
          email: created.email || fields.email,
        }, 'invite');
      }
      setShowAddModal(false);
      setNewName('');
      setOppStageForm({ name: '', is_won: false, is_lost: false });
      setRoleForm({ name: '', view_accounts: true, edit_accounts: false, delete_accounts: false, view_all_accounts: false, manage_users: false, manage_settings: false });
      setUserForm(EMPTY_USER_FORM);
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'Failed to add'); }
  };

  const showInviteOutcome = (invite, person, kind) => {
    if (!invite) return;
    if (invite.url) {
      setInviteLink({ url: invite.url, name: person.name, email: person.email, kind });
    } else if (invite.sent) {
      setInviteNotice(kind === 'reset' ? `Password reset link sent to ${person.email}.` : `Invite sent to ${person.email}.`);
    }
  };

  const handleInvite = async (u) => {
    const kind = u.invited_at ? 'invite' : 'reset';
    setError('');
    setInviteNotice('');
    setInvitingId(u.id);
    try {
      const res = await inviteUser(u.id);
      showInviteOutcome(res.data?.invite, u, kind);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || (kind === 'reset' ? 'Failed to send the password reset link' : 'Failed to send the invite'));
    } finally {
      setInvitingId(null);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      if (activeTab === 'stages') await updateStage(editItem.id, editItem);
      else if (activeTab === 'opportunity-stages') await updateOpportunityStage(editItem.id, editItem);
      else if (activeTab === 'lead-sources') await updateLeadSource(editItem.id, editItem.name);
      else if (activeTab === 'ownership-types') await updateOwnershipRoleType(editItem.id, editItem.name);
      else if (activeTab === 'roles') await updateRole(editItem.id, editItem);
      else if (activeTab === 'users') await updateUser(editItem.id, { first_name: editItem.first_name, last_name: editItem.last_name, alias: editItem.alias, email: editItem.email, role_id: editItem.role_id });
      setEditItem(null);
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'Failed to update'); }
  };

  const handleDelete = async () => {
    try {
      if (activeTab === 'stages') await deleteStage(deleteConfirm);
      else if (activeTab === 'opportunity-stages') await deleteOpportunityStage(deleteConfirm);
      else if (activeTab === 'lead-sources') await deleteLeadSource(deleteConfirm);
      else if (activeTab === 'ownership-types') await deleteOwnershipRoleType(deleteConfirm);
      else if (activeTab === 'roles') await deleteRole(deleteConfirm);
      setDeleteConfirm(null);
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'Failed to delete'); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await resetUserPassword(resetPasswordUser.id, newPassword);
      setResetPasswordUser(null);
      setNewPassword('');
      alert('Password reset successfully');
    } catch (err) { setError(err.response?.data?.error || 'Failed to reset password'); }
  };

  const handleDeactivate = async () => {
    try {
      await deactivateUser(deactivateConfirm);
      setDeactivateConfirm(null);
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'Failed to deactivate user'); }
  };

  const handleReactivate = async (userId) => {
    try {
      await reactivateUser(userId);
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'Failed to reactivate user'); }
  };

  const handleImpersonate = async (userId) => {
    try {
      await startImpersonation(userId);
      navigate('/');
    } catch (err) { setError(err.response?.data?.error || 'Failed to impersonate user'); }
  };

  const handleSaveCompanySettings = async (e) => {
    e.preventDefault();
    setCompanySaving(true);
    setCompanyMessage('');
    try {
      if (logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);
        const logoRes = await uploadCompanyLogo(formData);
        companySettings.logo_path = logoRes.data.logo_path;
      }
      await updateCompanySettings(companySettings);
      setCompanyMessage('Company settings saved successfully');
      setLogoFile(null);
      setTimeout(() => setCompanyMessage(''), 3000);
    } catch (err) {
      setCompanyMessage(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setCompanySaving(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  if (!hasPermission('manage_settings')) {
    return <div className="alert alert-error">You do not have permission to access settings.</div>;
  }

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  const items = activeTab === 'stages' ? stages : activeTab === 'opportunity-stages' ? opportunityStages : activeTab === 'lead-sources' ? leadSources : activeTab === 'ownership-types' ? ownershipTypes : activeTab === 'users' ? users : roles;

  // ── Sidebar navigation definition ──────────────────────────────────
  const sidebarGroups = [
    {
      label: 'GENERAL',
      items: [
        { key: 'company', label: 'Company Info', icon: Building2 },
        // Customer workspaces only: the per-user subscription through Stripe.
        ...(billingApplies ? [{ key: 'billing', label: 'Billing', icon: CreditCard }] : []),
        { key: 'module-management', label: 'Module Management', icon: SettingsIcon, navigateTo: '/module-management' },
      ],
    },
    {
      label: 'CRM CONFIGURATION',
      items: [
        { key: 'stages', label: 'Account Stages', icon: Layers },
        { key: 'opportunity-stages', label: 'Opportunity Stages', icon: Target },
        { key: 'lead-sources', label: 'Lead Sources', icon: ClipboardList },
        { key: 'ownership-types', label: 'Ownership Roles', icon: Users },
      ],
    },
    {
      label: 'SALES CHANNELS',
      items: [
        { key: 'rep-firms', label: 'Rep Firms', icon: Briefcase },
      ],
    },
    {
      label: 'USERS & PERMISSIONS',
      items: [
        ...(hasPermission('manage_users') ? [{ key: 'users', label: 'Users', icon: UserCog }] : []),
        { key: 'roles', label: 'User Roles', icon: Shield },
        { key: 'report-access', label: 'Report Access', icon: BarChart3 },
        ...(hasPermission('manage_users') ? [{ key: 'login-history', label: 'Login History', icon: History }] : []),
        ...(hasPermission('manage_users') ? [{ key: 'access-requests', label: 'Access Requests', icon: Inbox }] : []),
      ],
    },
    // Platform owner only: the customer companies, each with its own
    // workspace. Customer admins never see this group.
    ...(isPlatformOwner ? [{
      label: 'PLATFORM',
      items: [
        { key: 'customers', label: 'Customers', icon: Building },
      ],
    }] : []),
    {
      label: 'DOCUMENTS',
      items: [
        { key: 'quote-template', label: 'Quote PDF Template', icon: FileText, navigateTo: '/quote-template-editor' },
      ],
    },
    // AI capture: what the drafts from the phones are costing. The report
    // endpoint is gated on manage_settings, as is this whole page.
    {
      label: 'AI CAPTURE',
      items: [
        { key: 'ai-usage', label: 'AI Usage & Cost', icon: Sparkles },
      ],
    },
    // Only show the Danger Zone to users with manage_settings permission.
    // The corresponding server endpoint enforces the same gate, so this
    // is purely a UX nicety — non-admins wouldn't be able to call it
    // anyway.
    ...(hasPermission('manage_settings') ? [{
      label: 'DANGER ZONE',
      items: [
        { key: 'system-reset', label: 'System Reset', icon: AlertTriangle },
      ],
    }] : []),
  ];

  // ── Inline styles ──────────────────────────────────────────────────
  const styles = {
    pageWrapper: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    header: {
      marginBottom: '20px',
    },
    title: {
      fontSize: '24px',
      fontWeight: 700,
      color: 'var(--text-primary)',
      margin: 0,
    },
    subtitle: {
      fontSize: '14px',
      color: 'var(--text-secondary)',
      margin: '4px 0 0',
    },
    layout: {
      display: 'flex',
      gap: '0px',
      flex: 1,
      minHeight: 0,
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border-color)',
      overflow: 'hidden',
      boxShadow: 'var(--shadow-sm)',
    },
    sidebar: {
      width: '240px',
      minWidth: '240px',
      background: 'var(--bg-secondary)',
      borderRight: '1px solid var(--border-color)',
      overflowY: 'auto',
      padding: '8px 0',
    },
    groupLabel: {
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: 'var(--text-muted)',
      padding: '16px 16px 6px',
      margin: 0,
    },
    groupLabelFirst: {
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: 'var(--text-muted)',
      padding: '8px 16px 6px',
      margin: 0,
    },
    navItem: (isActive) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      width: 'calc(100% - 12px)',
      margin: '1px 6px',
      padding: '7px 10px',
      fontSize: '14px',
      color: isActive ? 'var(--accent-primary)' : 'var(--text-primary)',
      background: isActive ? 'var(--bg-tertiary)' : 'transparent',
      border: 'none',
      borderLeft: isActive ? '3px solid var(--accent-primary)' : '3px solid transparent',
      borderRadius: 'var(--radius-sm)',
      cursor: 'pointer',
      textAlign: 'left',
      fontWeight: isActive ? 600 : 400,
      transition: 'background 0.15s, color 0.15s',
    }),
    navItemIcon: (isActive) => ({
      flexShrink: 0,
      color: isActive ? 'var(--accent-primary)' : 'var(--text-secondary)',
    }),
    navItemLabel: {
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    externalArrow: {
      flexShrink: 0,
      color: 'var(--text-muted)',
    },
    contentPanel: {
      flex: 1,
      overflowY: 'auto',
      padding: '24px',
      background: 'var(--bg-primary)',
    },
  };

  // ── Sidebar click handler ─────────────────────────────────────────
  const handleNavClick = (item) => {
    if (item.navigateTo) {
      navigate(item.navigateTo);
    } else {
      setActiveSection(item.key);
    }
  };

  // ── Content panel rendering ────────────────────────────────────────
  const renderContent = () => {
    // Login History — admin audit log of all login attempts
    if (activeSection === 'ai-usage') {
      return <AIUsagePanel />;
    }

    // Requests from the landing page's Request access form
    if (activeSection === 'access-requests') {
      return <AccessRequestsPanel />;
    }

    // The workspace's subscription, for customer workspaces only
    if (activeSection === 'billing' && billingApplies) {
      return <BillingPanel />;
    }

    // Customer workspaces, for the platform owner only
    if (activeSection === 'customers' && isPlatformOwner) {
      return <CustomersPanel />;
    }

    if (activeSection === 'login-history') {
      const reasonLabels = { unknown_email: 'Unknown email', invalid_password: 'Wrong password' };
      const q = loginHistorySearch.trim().toLowerCase();
      const filtered = q
        ? loginHistory.filter(r =>
            (r.email_attempted || '').toLowerCase().includes(q) ||
            (r.user_name || '').toLowerCase().includes(q))
        : loginHistory;
      return (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
            <div>
              <h3 className="card-title">Login History</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
                Recent login attempts across all users — successful and failed.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => handleExportLoginHistory(filtered)}
              disabled={loginHistoryLoading || filtered.length === 0}
              title="Export the rows shown below to a CSV file"
              style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              <Download size={16} /> Export CSV
            </button>
          </div>
          <div className="card-body" style={{ paddingBottom: 0 }}>
            <div className="search-input" style={{ marginBottom: '16px' }}>
              <Search size={18} className="search-input-icon" aria-hidden="true" />
              <input
                type="text"
                className="form-input"
                placeholder="Filter by name or email..."
                value={loginHistorySearch}
                onChange={(e) => setLoginHistorySearch(e.target.value)}
              />
            </div>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Result</th>
                  <th scope="col">User</th>
                  <th scope="col">Email entered</th>
                  <th scope="col">IP address</th>
                  <th scope="col">Device</th>
                </tr>
              </thead>
              <tbody>
                {loginHistoryLoading ? (
                  <tr><td colSpan="6"><div className="loading-container"><div className="spinner"></div></div></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan="6"><div className="empty-state"><History size={48} className="empty-state-icon" /><h3 className="empty-state-title">No login attempts recorded</h3></div></td></tr>
                ) : (
                  filtered.map(r => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{new Date(r.created_at.replace(' ', 'T') + 'Z').toLocaleString()}</td>
                      <td>
                        {r.success ? (
                          <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><CheckCircle2 size={14} /> Success</span>
                        ) : (
                          <span className="badge badge-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }} title={reasonLabels[r.failure_reason] || r.failure_reason || 'Failed'}><XCircle size={14} /> Failed</span>
                        )}
                        {!r.success && r.failure_reason && (
                          <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '6px' }}>{reasonLabels[r.failure_reason] || r.failure_reason}</span>
                        )}
                      </td>
                      <td>{r.user_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td>{r.email_attempted || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{r.ip_address || '—'}</td>
                      <td style={{ maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.user_agent || ''}>{r.user_agent || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Company Info
    if (activeSection === 'company') {
      return (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Company Information</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
              This information appears on quote PDFs and other documents.
            </p>
          </div>
          <form onSubmit={handleSaveCompanySettings}>
            <div className="card-body">
              {companyMessage && (
                <div className={`alert ${companyMessage.includes('success') ? 'alert-success' : 'alert-error'}`}>
                  {companyMessage}
                </div>
              )}

              {/* Logo Upload */}
              <div className="form-group">
                <label className="form-label">Company Logo</label>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  {logoPreview && (
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '12px', background: 'var(--bg-tertiary)' }}>
                      <img src={logoPreview} alt="Company Logo" style={{ maxWidth: '200px', maxHeight: '80px', objectFit: 'contain' }} />
                    </div>
                  )}
                  <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                    <Upload size={16} /> {logoPreview ? 'Change Logo' : 'Upload Logo'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml"
                      onChange={handleLogoChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                  PNG, JPEG, or SVG. Max 2MB. Appears in quote PDF headers.
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
                <div className="form-group">
                  <label className="form-label">Company Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={companySettings.company_name || ''}
                    onChange={(e) => setCompanySettings({ ...companySettings, company_name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Tagline</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Light-Speed Connectivity"
                    value={companySettings.company_tagline || ''}
                    onChange={(e) => setCompanySettings({ ...companySettings, company_tagline: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Address</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Full company address"
                  value={companySettings.company_address || ''}
                  onChange={(e) => setCompanySettings({ ...companySettings, company_address: e.target.value })}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px 24px' }}>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={companySettings.company_email || ''}
                    onChange={(e) => setCompanySettings({ ...companySettings, company_email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Website</label>
                  <input
                    type="text"
                    className="form-input"
                    value={companySettings.company_website || ''}
                    onChange={(e) => setCompanySettings({ ...companySettings, company_website: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={companySettings.company_phone || ''}
                    onChange={(e) => setCompanySettings({ ...companySettings, company_phone: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" className="btn btn-primary" disabled={companySaving}>
                <Save size={16} /> {companySaving ? 'Saving...' : 'Save Company Settings'}
              </button>
            </div>
          </form>
        </div>
      );
    }

    // Rep Firms
    if (activeSection === 'rep-firms') {
      const filteredRepFirms = repFirms.filter(rf =>
        rf.name.toLowerCase().includes(repFirmSearch.toLowerCase())
      );
      return (
        <>
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Rep Firms</h3>
              <button className="btn btn-primary btn-sm" onClick={() => setShowRepFirmModal(true)}><Plus size={16} /> New Rep Firm</button>
            </div>
            <div className="card-body" style={{ paddingBottom: 0 }}>
              <div className="search-input" style={{ marginBottom: '16px' }}>
                <Search size={18} className="search-input-icon" aria-hidden="true" />
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search rep firms..."
                  value={repFirmSearch}
                  onChange={(e) => setRepFirmSearch(e.target.value)}
                  aria-label="Search rep firms"
                />
              </div>
            </div>
            <div className="table-container">
              <table className="table" aria-label="Rep firms list">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Phone</th>
                    <th scope="col">Country</th>
                    <th scope="col">State</th>
                    <th scope="col" className="col-actions"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRepFirms.length === 0 ? (
                    <tr>
                      <td colSpan={5}>
                        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)' }}>
                          <Briefcase size={32} style={{ opacity: 0.3, marginBottom: '8px' }} />
                          <div>No rep firms found</div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredRepFirms.map(rf => (
                      <tr key={rf.id}>
                        <td><Link to={`/rep-firms/${rf.id}`} style={{ color: 'var(--accent-primary)', fontWeight: 500 }}>{rf.name}</Link></td>
                        <td>{rf.phone || '-'}</td>
                        <td>{rf.country || '-'}</td>
                        <td>{rf.state || '-'}</td>
                        <td>
                          <Link to={`/rep-firms/${rf.id}`} className="btn btn-icon" title="View Details" aria-label={`View ${rf.name}`}><Edit size={16} /></Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Create Rep Firm Modal */}
          <Modal isOpen={showRepFirmModal} onClose={() => { setShowRepFirmModal(false); setRepFirmForm({ name: '', website: '', phone: '', address: '', country: '', state: '', notes: '' }); }} title="New Rep Firm">
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                await createRepFirm(repFirmForm);
                setShowRepFirmModal(false);
                setRepFirmForm({ name: '', website: '', phone: '', address: '', country: '', state: '', notes: '' });
                const res = await getRepFirms({});
                setRepFirms(res.data);
              } catch (err) { setError(err.response?.data?.error || 'Failed to create rep firm'); }
            }}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input type="text" className="form-input" value={repFirmForm.name} onChange={(e) => setRepFirmForm({ ...repFirmForm, name: e.target.value })} required />
                </div>
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Website</label>
                    <input type="url" className="form-input" value={repFirmForm.website} onChange={(e) => setRepFirmForm({ ...repFirmForm, website: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input type="tel" className="form-input" value={repFirmForm.phone} onChange={(e) => setRepFirmForm({ ...repFirmForm, phone: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <input type="text" className="form-input" value={repFirmForm.address} onChange={(e) => setRepFirmForm({ ...repFirmForm, address: e.target.value })} />
                </div>
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Country</label>
                    <input type="text" className="form-input" value={repFirmForm.country} onChange={(e) => setRepFirmForm({ ...repFirmForm, country: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State</label>
                    <input type="text" className="form-input" value={repFirmForm.state} onChange={(e) => setRepFirmForm({ ...repFirmForm, state: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" value={repFirmForm.notes} onChange={(e) => setRepFirmForm({ ...repFirmForm, notes: e.target.value })} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowRepFirmModal(false); setRepFirmForm({ name: '', website: '', phone: '', address: '', country: '', state: '', notes: '' }); }}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create</button>
              </div>
            </form>
          </Modal>
        </>
      );
    }

    // Report Access section
    if (activeSection === 'report-access') {
      const REPORT_TYPES_LIST = [
        { key: 'sales_pipeline', label: 'Sales Pipeline' },
        { key: 'revenue', label: 'Revenue' },
        { key: 'forecast', label: 'Forecast' },
      ];

      const loadReportAccessData = async () => {
        try {
          const res = await getReportAccess();
          setReportAccess(res.data);
        } catch (err) {
          console.error('Failed to load report access:', err);
        }
      };

      if (reportAccess.length === 0) {
        loadReportAccessData();
      }

      const handleToggleAccess = (roleId, reportType) => {
        setReportAccess(prev =>
          prev.map(item =>
            item.role_id === roleId && item.report_type === reportType
              ? { ...item, allowed: item.allowed ? 0 : 1 }
              : item
          )
        );
      };

      const handleSaveReportAccess = async () => {
        setReportAccessSaving(true);
        try {
          await updateReportAccess(reportAccess.map(({ role_id, report_type, allowed }) => ({
            role_id, report_type, allowed
          })));
          setError('');
        } catch (err) {
          setError('Failed to save report access');
        } finally {
          setReportAccessSaving(false);
        }
      };

      // Group by role
      const roleMap = {};
      reportAccess.forEach(item => {
        if (!roleMap[item.role_id]) roleMap[item.role_id] = { name: item.role_name, items: {} };
        roleMap[item.role_id].items[item.report_type] = item.allowed;
      });

      return (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 className="card-title">Report Access Control</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
                Control which roles can access each report type. Admin role always has full access.
              </p>
            </div>
            <button className="btn btn-primary" onClick={handleSaveReportAccess} disabled={reportAccessSaving}>
              <Save size={16} /> {reportAccessSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
          <div className="card-body">
            <div className="table-container">
              <table className="table" aria-label="Report access control">
                <thead>
                  <tr>
                    <th>Role</th>
                    {REPORT_TYPES_LIST.map(rt => (
                      <th key={rt.key} style={{ textAlign: 'center' }}>{rt.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(roleMap).map(([roleId, role]) => (
                    <tr key={roleId}>
                      <td style={{ fontWeight: 500 }}>{role.name}</td>
                      {REPORT_TYPES_LIST.map(rt => (
                        <td key={rt.key} style={{ textAlign: 'center' }}>
                          {role.name === 'Admin' ? (
                            <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>Always</span>
                          ) : (
                            <label style={{ cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={!!role.items[rt.key]}
                                onChange={() => handleToggleAccess(Number(roleId), rt.key)}
                                style={{ width: 18, height: 18 }}
                              />
                            </label>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    }

    // System Reset (Danger Zone) — admin-only destructive action.
    // Shows what will be deleted and requires the user to type a
    // confirmation phrase before the button is enabled.
    if (activeSection === 'system-reset') {
      const expectedPhrase = resetPreview?.confirm_phrase || 'DELETE ALL DATA';
      const phraseOK = resetPhrase === expectedPhrase;
      const tables = resetPreview?.tables || {};
      const preserved = resetPreview?.preserved || {};

      const formatLabel = (key) => key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());

      return (
        <div className="card">
          <div className="card-header" style={{ borderLeft: '4px solid var(--accent-danger)' }}>
            <div>
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={20} color="var(--accent-danger)" /> System Reset
              </h3>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
                Wipes all business data and restores the system to factory defaults. This cannot be undone (apart from restoring the .db backup the server creates automatically).
              </p>
            </div>
          </div>
          <div className="card-body">
            {/* What gets wiped */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, margin: '0 0 8px', color: 'var(--text-primary)' }}>What will be deleted</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', fontSize: 13, color: 'var(--text-secondary)' }}>
                {Object.entries(tables).map(([t, n]) => (
                  <div key={t} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid var(--border-color)' }}>
                    <span>{formatLabel(t)}</span>
                    <span style={{ fontWeight: 600, color: n > 0 ? 'var(--accent-danger)' : 'var(--text-muted)' }}>{n.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              {resetPreview && (
                <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-primary)' }}>
                  <strong>{(resetPreview.total_rows_to_delete || 0).toLocaleString()}</strong> rows total will be deleted.
                </div>
              )}
            </div>

            {/* What's preserved */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, margin: '0 0 8px', color: 'var(--text-primary)' }}>What stays</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', fontSize: 13, color: 'var(--text-secondary)' }}>
                {Object.entries(preserved).map(([t, n]) => (
                  <div key={t} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid var(--border-color)' }}>
                    <span>{formatLabel(t)}</span>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{n.toLocaleString()}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid var(--border-color)' }}>
                  <span>Company Settings</span>
                  <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>reset to default</span>
                </div>
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0' }}>
                Users, roles, permissions, and your configuration (stages, lead sources, opportunity stages, ownership role types, templates) are preserved. You will stay logged in.
              </p>
            </div>

            {/* Backup note */}
            <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 'var(--radius-md)', padding: 12, fontSize: 13, color: '#78350f', marginBottom: 16 }}>
              <strong>Before resetting:</strong> the server copies the current database to <code>server/backups/factory_reset_&lt;timestamp&gt;.db</code>. If something goes wrong, an engineer can restore from that file. There is no automatic restore from the UI.
            </div>

            {/* Confirmation phrase */}
            <div className="form-group" style={{ maxWidth: 420 }}>
              <label className="form-label" style={{ color: 'var(--accent-danger)' }}>
                Type <code>{expectedPhrase}</code> to confirm
              </label>
              <input
                type="text"
                className="form-input"
                value={resetPhrase}
                onChange={(e) => { setResetPhrase(e.target.value); setResetError(''); }}
                placeholder={expectedPhrase}
                autoComplete="off"
                spellCheck={false}
                style={{ fontFamily: 'monospace' }}
              />
            </div>

            {resetError && (
              <div className="alert alert-error" role="alert" style={{ marginTop: 12 }}>{resetError}</div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                className="btn btn-secondary"
                onClick={async () => {
                  setResetError('');
                  setResetPhrase('');
                  try {
                    const res = await getFactoryResetPreview();
                    setResetPreview(res.data);
                  } catch (err) {
                    setResetError(err.response?.data?.error || 'Could not load preview');
                  }
                }}
              >
                Refresh counts
              </button>
              <button
                className="btn btn-danger"
                disabled={!phraseOK || resetSaving}
                onClick={() => { setResetError(''); setShowResetModal(true); }}
                title={phraseOK ? 'Open final confirmation' : `Type "${expectedPhrase}" first`}
              >
                <AlertTriangle size={16} /> {resetSaving ? 'Resetting…' : 'Reset System'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // List-based sections (stages, opportunity-stages, lead-sources, ownership-types, roles, users)
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            {activeTab === 'stages' && 'Account Stages'}
            {activeTab === 'opportunity-stages' && 'Opportunity Stages'}
            {activeTab === 'lead-sources' && 'Lead Sources'}
            {activeTab === 'ownership-types' && 'Ownership Role Types'}
            {activeTab === 'roles' && 'User Roles'}
            {activeTab === 'users' && 'Users'}
          </h3>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)}><Plus size={16} /> {activeTab === 'users' ? 'New User' : 'Add'}</button>
        </div>
        <div className="table-container">
          <table className="table" aria-label="Settings items list">
            <thead>
              <tr>
                <th scope="col">Name</th>
                {activeTab === 'opportunity-stages' && <th scope="col">Type</th>}
                {activeTab === 'roles' && <th scope="col">Permissions</th>}
                {activeTab === 'users' && <th scope="col">Email</th>}
                {activeTab === 'users' && <th scope="col">Role</th>}
                {activeTab === 'users' && <th scope="col">Status</th>}
                <th scope="col" style={{ width: activeTab === 'users' ? 220 : 100 }}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id}>
                  <td>
                    {item.name}
                    {activeTab === 'users' && item.alias && <span className="text-sm text-secondary ml-2">“{item.alias}”</span>}
                    {activeTab === 'users' && item.id === currentUser.id && <span className="badge badge-primary ml-2">You</span>}
                  </td>
                  {activeTab === 'opportunity-stages' && (
                    <td>
                      {item.is_won ? <span className="badge badge-success">Won</span> : item.is_lost ? <span className="badge badge-danger">Lost</span> : <span className="badge badge-gray">Open</span>}
                    </td>
                  )}
                  {activeTab === 'roles' && (
                    <td className="text-sm text-secondary">
                      {item.view_accounts && 'View'} {item.edit_accounts && '| Edit'} {item.delete_accounts && '| Delete'} {item.view_all_accounts && '| View All'} {item.manage_users && '| Users'} {item.manage_settings && '| Settings'}
                    </td>
                  )}
                  {activeTab === 'users' && <td>{item.email}</td>}
                  {activeTab === 'users' && <td>{item.role_name || '-'}</td>}
                  {activeTab === 'users' && (
                    <td>
                      <span className={`badge ${item.active ? 'badge-success' : 'badge-gray'}`}>{item.active ? 'Active' : 'Inactive'}</span>
                      {item.invited_at && <span className="badge badge-warning ml-2" title="Invite sent; they have not set a password yet">Invited</span>}
                    </td>
                  )}
                  <td>
                    <button className="btn btn-icon" onClick={() => setEditItem({ ...item })} title="Edit" aria-label={`Edit ${item.name}`}><Edit size={16} /></button>
                    {activeTab === 'users' ? (
                      <>
                        <button className="btn btn-icon" onClick={() => navigate(`/users/${item.id}/permissions`)} title="Permissions" aria-label={`Manage permissions for ${item.name}`}><Shield size={16} /></button>
                        <button className="btn btn-icon" onClick={() => setResetPasswordUser(item)} title="Reset Password" aria-label={`Reset password for ${item.name}`}><Key size={16} /></button>
                        {item.active && (
                          <button className="btn btn-icon" onClick={() => handleInvite(item)} disabled={invitingId === item.id} title={inviteActionLabel(item)} aria-label={`${inviteActionLabel(item)} to ${item.name}`}><Mail size={16} /></button>
                        )}
                        {!isImpersonating && item.id !== currentUser.id && item.active && item.role_name !== 'Admin' && (
                          <button className="btn btn-icon" onClick={() => handleImpersonate(item.id)} title="View as this user" aria-label={`View as ${item.name}`}><Eye size={16} /></button>
                        )}
                        {item.id !== currentUser.id && (
                          item.active ? (
                            <button className="btn btn-icon" onClick={() => setDeactivateConfirm(item.id)} title="Deactivate" aria-label={`Deactivate ${item.name}`}><UserX size={16} /></button>
                          ) : (
                            <button className="btn btn-icon" onClick={() => handleReactivate(item.id)} title="Reactivate" aria-label={`Reactivate ${item.name}`}><UserCheck size={16} /></button>
                          )
                        )}
                      </>
                    ) : (
                      <button className="btn btn-icon" onClick={() => setDeleteConfirm(item.id)} title="Delete" aria-label={`Delete ${item.name}`}><Trash2 size={16} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.header}>
        <h1 style={styles.title}>Administration</h1>
        <p style={styles.subtitle}>Manage your CRM configuration, users, and preferences</p>
      </div>

      <EmailVerificationNotice />

      {error && <div className="alert alert-error" role="alert" aria-live="assertive" style={{ marginBottom: '16px' }}>{error}</div>}
      {inviteNotice && activeTab === 'users' && <div className="alert alert-success" role="status" style={{ marginBottom: '16px' }}>{inviteNotice}</div>}

      <div style={styles.layout}>
        {/* ── Sidebar ── */}
        <nav style={styles.sidebar}>
          {sidebarGroups.map((group, gi) => (
            <div key={group.label}>
              <div style={gi === 0 ? styles.groupLabelFirst : styles.groupLabel}>{group.label}</div>
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = !item.navigateTo && activeSection === item.key;
                return (
                  <button
                    key={item.key}
                    style={styles.navItem(isActive)}
                    onClick={() => handleNavClick(item)}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.background = 'var(--bg-tertiary)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <Icon size={16} style={styles.navItemIcon(isActive)} />
                    <span style={styles.navItemLabel}>{item.label}</span>
                    {item.navigateTo && <ChevronRight size={14} style={styles.externalArrow} />}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* ── Content Panel ── */}
        <div style={styles.contentPanel}>
          {renderContent()}
        </div>
      </div>

      {/* Add Modal */}
      <Modal isOpen={showAddModal} onClose={() => { setShowAddModal(false); setNewName(''); setOppStageForm({ name: '', is_won: false, is_lost: false }); setUserForm(EMPTY_USER_FORM); }} title={`${activeTab === 'users' ? 'New User' : 'Add'} ${activeTab === 'stages' ? 'Account Stage' : activeTab === 'opportunity-stages' ? 'Opportunity Stage' : activeTab === 'lead-sources' ? 'Lead Source' : activeTab === 'ownership-types' ? 'Ownership Role' : activeTab === 'users' ? '' : 'Role'}`}>
        <form onSubmit={handleAdd}>
          <div className="modal-body">
            {activeTab === 'users' ? (
              <>
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">First name *</label>
                    <input type="text" className="form-input" value={userForm.first_name} onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })} required autoComplete="off" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last name</label>
                    <input type="text" className="form-input" value={userForm.last_name} onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })} autoComplete="off" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Alias</label>
                  <input type="text" className="form-input" value={userForm.alias} onChange={(e) => setUserForm({ ...userForm, alias: e.target.value })} placeholder="What people call them, if not their first name" autoComplete="off" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input type="email" className="form-input" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="checkbox-wrapper">
                    <input type="checkbox" className="checkbox" checked={userForm.send_invite} onChange={(e) => setUserForm({ ...userForm, send_invite: e.target.checked })} />
                    Email an invite instead of setting a password
                  </label>
                  {userForm.send_invite && (
                    <p className="text-sm text-secondary" style={{ margin: '4px 0 0' }}>They get a link to choose their own password.</p>
                  )}
                </div>
                {!userForm.send_invite && (
                  <div className="form-group">
                    <label className="form-label">Password *</label>
                    <input type="password" className="form-input" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} required minLength="6" />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="form-select" value={userForm.role_id} onChange={(e) => setUserForm({ ...userForm, role_id: e.target.value })}>
                    <option value="">Select Role</option>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input type="text" className="form-input" value={activeTab === 'roles' ? roleForm.name : activeTab === 'opportunity-stages' ? oppStageForm.name : newName} onChange={(e) => activeTab === 'roles' ? setRoleForm({ ...roleForm, name: e.target.value }) : activeTab === 'opportunity-stages' ? setOppStageForm({ ...oppStageForm, name: e.target.value }) : setNewName(e.target.value)} required />
                </div>
                {activeTab === 'opportunity-stages' && (
                  <div className="form-group">
                    <label className="form-label">Stage Type</label>
                    <div className="grid grid-cols-2">
                      <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={oppStageForm.is_won} onChange={(e) => setOppStageForm({ ...oppStageForm, is_won: e.target.checked, is_lost: e.target.checked ? false : oppStageForm.is_lost })} /> Won Stage</label>
                      <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={oppStageForm.is_lost} onChange={(e) => setOppStageForm({ ...oppStageForm, is_lost: e.target.checked, is_won: e.target.checked ? false : oppStageForm.is_won })} /> Lost Stage</label>
                    </div>
                  </div>
                )}
                {activeTab === 'roles' && (
                  <div className="form-group">
                    <label className="form-label">Permissions</label>
                    <div className="grid grid-cols-2">
                      <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={roleForm.view_accounts} onChange={(e) => setRoleForm({ ...roleForm, view_accounts: e.target.checked })} /> View Accounts</label>
                      <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={roleForm.edit_accounts} onChange={(e) => setRoleForm({ ...roleForm, edit_accounts: e.target.checked })} /> Edit Accounts</label>
                      <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={roleForm.delete_accounts} onChange={(e) => setRoleForm({ ...roleForm, delete_accounts: e.target.checked })} /> Delete Accounts</label>
                      <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={roleForm.view_all_accounts} onChange={(e) => setRoleForm({ ...roleForm, view_all_accounts: e.target.checked })} /> View All Accounts</label>
                      <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={roleForm.manage_users} onChange={(e) => setRoleForm({ ...roleForm, manage_users: e.target.checked })} /> Manage Users</label>
                      <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={roleForm.manage_settings} onChange={(e) => setRoleForm({ ...roleForm, manage_settings: e.target.checked })} /> Manage Settings</label>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowAddModal(false); setNewName(''); setOppStageForm({ name: '', is_won: false, is_lost: false }); setUserForm(EMPTY_USER_FORM); }}>Cancel</button>
            <button type="submit" className="btn btn-primary">{activeTab === 'users' ? 'Create User' : 'Add'}</button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={!!editItem} onClose={() => setEditItem(null)} title={activeTab === 'users' ? 'Edit User' : 'Edit'}>
        {editItem && (
          <form onSubmit={handleUpdate}>
            <div className="modal-body">
              {activeTab === 'users' ? (
                <>
                  <div className="grid grid-cols-2">
                    <div className="form-group">
                      <label className="form-label">First name *</label>
                      <input type="text" className="form-input" value={editItem.first_name || ''} onChange={(e) => setEditItem({ ...editItem, first_name: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last name</label>
                      <input type="text" className="form-input" value={editItem.last_name || ''} onChange={(e) => setEditItem({ ...editItem, last_name: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Alias</label>
                    <input type="text" className="form-input" value={editItem.alias || ''} onChange={(e) => setEditItem({ ...editItem, alias: e.target.value })} placeholder="What people call them, if not their first name" />
                  </div>
                </>
              ) : (
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input type="text" className="form-input" value={editItem.name} onChange={(e) => setEditItem({ ...editItem, name: e.target.value })} required />
                </div>
              )}
              {activeTab === 'users' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input type="email" className="form-input" value={editItem.email} onChange={(e) => setEditItem({ ...editItem, email: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select className="form-select" value={editItem.role_id || ''} onChange={(e) => setEditItem({ ...editItem, role_id: e.target.value })}>
                      <option value="">Select Role</option>
                      {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                </>
              )}
              {activeTab === 'opportunity-stages' && (
                <div className="form-group">
                  <label className="form-label">Stage Type</label>
                  <div className="grid grid-cols-2">
                    <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={editItem.is_won} onChange={(e) => setEditItem({ ...editItem, is_won: e.target.checked, is_lost: e.target.checked ? false : editItem.is_lost })} /> Won Stage</label>
                    <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={editItem.is_lost} onChange={(e) => setEditItem({ ...editItem, is_lost: e.target.checked, is_won: e.target.checked ? false : editItem.is_won })} /> Lost Stage</label>
                  </div>
                </div>
              )}
              {activeTab === 'roles' && (
                <div className="form-group">
                  <label className="form-label">Permissions</label>
                  <div className="grid grid-cols-2">
                    <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={editItem.view_accounts} onChange={(e) => setEditItem({ ...editItem, view_accounts: e.target.checked })} /> View Accounts</label>
                    <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={editItem.edit_accounts} onChange={(e) => setEditItem({ ...editItem, edit_accounts: e.target.checked })} /> Edit Accounts</label>
                    <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={editItem.delete_accounts} onChange={(e) => setEditItem({ ...editItem, delete_accounts: e.target.checked })} /> Delete Accounts</label>
                    <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={editItem.view_all_accounts} onChange={(e) => setEditItem({ ...editItem, view_all_accounts: e.target.checked })} /> View All Accounts</label>
                    <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={editItem.manage_users} onChange={(e) => setEditItem({ ...editItem, manage_users: e.target.checked })} /> Manage Users</label>
                    <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={editItem.manage_settings} onChange={(e) => setEditItem({ ...editItem, manage_settings: e.target.checked })} /> Manage Settings</label>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setEditItem(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Save</button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} onConfirm={handleDelete} title="Delete" message="Are you sure you want to delete this item? Items in use cannot be deleted." confirmText="Delete" />

      {/* Reset Password Modal */}
      <Modal isOpen={!!resetPasswordUser} onClose={() => { setResetPasswordUser(null); setNewPassword(''); }} title="Reset Password">
        {resetPasswordUser && (
          <form onSubmit={handleResetPassword}>
            <div className="modal-body">
              <p className="mb-4">Reset password for <strong>{resetPasswordUser.name}</strong></p>
              <div className="form-group">
                <label className="form-label">New Password *</label>
                <input type="password" className="form-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength="6" />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => { setResetPasswordUser(null); setNewPassword(''); }}>Cancel</button>
              <button type="submit" className="btn btn-primary">Reset Password</button>
            </div>
          </form>
        )}
      </Modal>

      <InviteLinkDialog result={inviteLink} onClose={() => setInviteLink(null)} />

      <ConfirmDialog isOpen={!!deactivateConfirm} onClose={() => setDeactivateConfirm(null)} onConfirm={handleDeactivate} title="Deactivate User" message="Deactivate this user? They will no longer be able to log in." confirmText="Deactivate" />

      {/* Factory-reset final confirmation modal.
          Phrase has already been validated by the time this opens, but
          we double-check at submit time so a stale form can't slip
          through after a re-render. */}
      <Modal
        isOpen={showResetModal}
        onClose={() => { if (!resetSaving) setShowResetModal(false); }}
        title="Confirm system reset"
        size="md"
      >
        <div className="modal-body">
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
            <AlertTriangle size={28} color="var(--accent-danger)" style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                This is your last warning.
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                <strong>{(resetPreview?.total_rows_to_delete || 0).toLocaleString()}</strong> rows
                will be permanently deleted across {Object.keys(resetPreview?.tables || {}).length} tables.
                A database backup will be saved on the server before the reset runs, but the
                application offers no way to restore from it — an engineer would have to do that manually.
              </div>
            </div>
          </div>

          {resetError && (
            <div className="alert alert-error" role="alert" style={{ marginTop: 12 }}>{resetError}</div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowResetModal(false)}
            disabled={resetSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={performFactoryReset}
            disabled={resetSaving || resetPhrase !== (resetPreview?.confirm_phrase || 'DELETE ALL DATA')}
          >
            {resetSaving ? 'Resetting…' : 'Yes, reset the system'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default Settings;
